import { useState } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import type { PointRule } from "@core/types";
import {
  applyApiFieldErrors,
  markErrorHandled,
  reportError,
} from "@portal/common/errorMessage";
import { tokens } from "@core/theme/tokens";
import {
  AlertText,
  DialogActions,
  DialogHeader,
  EditorModal,
  Field,
  ImpactConfirmDialog,
  InfoNotice,
  inputStyle,
  PortalButton,
  selectStyle,
  useToast,
} from "@portal/common";
import { useActionCatalog, usePointRuleMutations } from "./rulesQueries";
import type { RuleScope } from "./rulesApi";
import {
  buildRuleInput,
  CAP_FREQUENCIES,
  CAP_INCOMPLETE_MESSAGE,
  CAP_PERIOD_HINT,
  capFrequencyLabelOf,
  CONFIRM_INACTIVATE_MESSAGE,
  defaultRuleFormValues,
  OVERLAPPING_VALIDITY_MESSAGE,
  ruleFormSchema,
  VALIDITY_REQUIRED_MESSAGE,
  type RuleFormValues,
} from "./rulesSchemas";

// B (human-resolution-spec-212.json): ambas cajas se copian literalmente,
// una por vista, sin agregar una segunda caja al editor de referido.
const MEMBER_FUTURE_CHANGES_HELP =
  "Los cambios aplican solo a movimientos futuros. Los puntos ya otorgados conservan el valor vigente al momento de su registro.";
const REFERRAL_ANEXO_014_HELP =
  "Cada acción otorga puntos una vez por registro. La acción final (Compra) es la única que puede registrarse más de una vez por referido — cada compra repite sus puntos, sin importar cuántas unidades declare. El miembro gana únicamente los puntos de las acciones efectivamente registradas; no hay liquidación de acciones no realizadas.";

function catalogEntryLabel(name: { es?: string } | string): string {
  return typeof name === "string" ? name : (name.es ?? "");
}

function defaultsFromRule(rule: PointRule | null): RuleFormValues {
  if (!rule) return defaultRuleFormValues();
  return {
    actionId: rule.action.id,
    points: rule.points,
    capFrequencyLabel: capFrequencyLabelOf(rule.capFrequency),
    capQuantity: rule.capQuantity ?? null,
    validFrom: rule.validFrom,
    validUntil: rule.validUntil ?? "",
    assignment: rule.assignment,
    active: rule.active,
  };
}

// Campos del formulario que muestran un error propio bajo su input.
const SERVER_FIELD_KEYS = ["points", "capQuantity", "validFrom"] as const;

type ServerFieldError = {
  field: "capQuantity" | "validFrom" | "validity";
  message: string;
};

export function RuleEditorModal({
  scope,
  rule,
  onClose,
  canCreate,
  canEdit,
}: {
  scope: RuleScope;
  rule: PointRule | null;
  onClose: () => void;
  canCreate: boolean;
  canEdit: boolean;
}) {
  const toast = useToast();
  const isCreate = rule === null;
  const catalog = useActionCatalog(scope, isCreate);
  const mutations = usePointRuleMutations();
  const [serverError, setServerError] = useState<ServerFieldError | null>(null);
  const [pendingInactivate, setPendingInactivate] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<RuleFormValues>({ defaultValues: defaultsFromRule(rule) });

  const capFrequencyLabel = watch("capFrequencyLabel");
  const active = watch("active");
  const submitting = mutations.create.isPending || mutations.edit.isPending;
  const canWrite = isCreate ? canCreate : canEdit;

  const handleServerError = (error: unknown) => {
    if (!(error instanceof ApiError)) return;
    // QA-MTZ-002 #11: la API identifica por campo el tope o los puntos
    // inválidos (400 INVALID_REQUEST con `errors[].field`): el texto va
    // bajo su input en vez del aviso genérico.
    if (
      applyApiFieldErrors(
        error,
        (field, fieldError) =>
          setError(field as keyof RuleFormValues, fieldError),
        SERVER_FIELD_KEYS,
      )
    ) {
      markErrorHandled(error);
      return;
    }
    // Cada rechazo con mensaje propio bajo su campo se marca atendido: sin
    // eso, el aviso genérico de `queryErrorFallback` salía además en el toast.
    if (error.code === "VALIDITY_REQUIRED") {
      setServerError({
        field: "validFrom",
        message: VALIDITY_REQUIRED_MESSAGE,
      });
      markErrorHandled(error);
      return;
    }
    if (error.code === "CAP_INCOMPLETE") {
      setServerError({ field: "capQuantity", message: CAP_INCOMPLETE_MESSAGE });
      markErrorHandled(error);
      return;
    }
    if (error.code === "OVERLAPPING_VALIDITY") {
      setServerError({
        field: "validity",
        message: OVERLAPPING_VALIDITY_MESSAGE,
      });
      markErrorHandled(error);
    }
  };

  const submit = (values: RuleFormValues) => {
    const parsed = ruleFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof RuleFormValues;
        setError(field, { message: issue.message });
      }
      return;
    }
    setServerError(null);
    // Scope referido: sin control de asignación/tope en el editor (el
    // prototipo, admin.jsx AdminMotorReferido, no los modela) — se envía el
    // valor requerido por el contrato sin exponer un widget (GEN-212).
    const input = buildRuleInput(
      scope === "referral"
        ? {
            ...parsed.data,
            assignment: "automatic",
            capFrequencyLabel: "Sin tope",
            capQuantity: null,
          }
        : parsed.data,
    );
    const onSuccess = () => {
      toast.show(
        rule ? "Regla guardada · aplica a movimientos futuros" : "Regla creada",
      );
      onClose();
    };
    if (rule) {
      mutations.edit.mutate(
        { id: rule.id, input },
        { onSuccess, onError: handleServerError },
      );
      return;
    }
    mutations.create.mutate(input, { onSuccess, onError: handleServerError });
  };

  const confirmInactivate = () => {
    if (!rule) return;
    mutations.inactivate.mutate(
      { id: rule.id, confirmed: true },
      {
        onSuccess: () => {
          setPendingInactivate(false);
          toast.show("Regla guardada · aplica a movimientos futuros");
          onClose();
        },
        // F2 (retry-category review_finding, 2026-09-07): el 403 de un
        // perfil sin `canInactivate` no tenía manejo. PLAN-009 §3: el
        // rechazo de servicio va al toast — sin código propio para este
        // rechazo, el resolutor compartido trae el genérico con
        // identificador de correlación.
        onError: (error) => reportError(toast, error),
      },
    );
  };

  const options = catalog.data ?? [];

  return (
    // El diálogo de impacto queda FUERA del `EditorModal`, no dentro de su
    // cuerpo: así no es descendiente del `<form>` que `EditorModal` envuelve
    // —ningún botón suyo puede enviarlo— ni de la región con `overflow`,
    // que es donde el recorte de un elemento `fixed` depende de dónde cae su
    // bloque contenedor. Es el mismo lugar en que lo montan `UsersScreen` y
    // `OffersScreen`, sus otros dos consumidores.
    //
    // Y las tres regiones del `Modal` por medio de `EditorModal`, que aporta
    // el `<form>` con `display: contents` — "Guardar regla" (`type="submit"`)
    // vive ahora en el pie fijo y sigue siendo descendiente del formulario.
    // El título sube al encabezado fijo: sale de adentro del
    // `fieldset disabled={!canWrite}`, lo que no cambia nada —un `fieldset`
    // deshabilitado no afecta a un `<h3>`, solo a los controles— y el pie
    // pasa a `DialogActions` en vez del `.btn-link` crudo de "Cancelar".
    <>
      <EditorModal
        modal={{
          label: rule ? "Editar regla" : "Nueva regla",
          width: scope === "member" ? 620 : 560,
          onClose,
        }}
        onSubmit={handleSubmit(submit)}
        // Punto #21 (revisión en vivo 2026-09-22): encabezado común.
        header={
          <DialogHeader
            eyebrow="Motor de Puntos"
            title={rule ? "Editar regla" : "Nueva regla"}
          />
        }
        footer={
          <DialogActions onCancel={onClose}>
            <Can
              module="points_engine"
              action={isCreate ? "canCreate" : "canEdit"}
            >
              <PortalButton type="submit" size="small" disabled={submitting}>
                {submitting ? "Guardando…" : "Guardar regla"}
              </PortalButton>
            </Can>
          </DialogActions>
        }
      >
        <fieldset
          disabled={!canWrite}
          style={{ border: "none", margin: 0, padding: 0 }}
        >
          {isCreate ? (
            <Field
              label={
                scope === "member"
                  ? "Acción del miembro"
                  : "Acción del referido"
              }
              required
            >
              {catalog.isError ? (
                // Punto #4C: mismo `role="alert"`, misma tipografía y mismo
                // color — pero desde `AlertText`, que es donde ya vivían esos
                // valores, en vez de una copia inline más.
                <AlertText>
                  No se pudo cargar el catálogo de acciones.
                </AlertText>
              ) : (
                <select
                  style={selectStyle}
                  {...register("actionId")}
                  aria-label={
                    scope === "member"
                      ? "Acción del miembro"
                      : "Acción del referido"
                  }
                >
                  <option value="">Selecciona una acción…</option>
                  {options.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {catalogEntryLabel(entry.name)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : (
            <Field
              label={
                scope === "member"
                  ? "Acción del miembro"
                  : "Acción del referido"
              }
              hint={
                scope === "referral"
                  ? "Viene del catálogo de acciones del referido."
                  : undefined
              }
            >
              <input
                style={{ ...inputStyle, opacity: 0.6 }}
                disabled
                value={rule?.action.name ?? ""}
              />
            </Field>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: scope === "member" ? "1fr 1fr 1fr" : "1fr",
              gap: "0 14px",
            }}
          >
            <Field label="Puntos" required error={errors.points?.message}>
              <input
                type="number"
                min={0}
                style={inputStyle}
                {...register("points", { valueAsNumber: true })}
              />
            </Field>
            {scope === "member" ? (
              <Field label="Frecuencia del tope">
                <select style={selectStyle} {...register("capFrequencyLabel")}>
                  {CAP_FREQUENCIES.map((freq) => (
                    <option key={freq} value={freq}>
                      {freq}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {scope === "member" ? (
              <Field
                label="Tope por período"
                hint={
                  capFrequencyLabel === "Sin tope"
                    ? "No aplica"
                    : CAP_PERIOD_HINT
                }
                error={
                  errors.capQuantity?.message ??
                  (serverError?.field === "capQuantity"
                    ? serverError.message
                    : undefined)
                }
              >
                <input
                  type="number"
                  min={1}
                  disabled={capFrequencyLabel === "Sin tope"}
                  style={{
                    ...inputStyle,
                    opacity: capFrequencyLabel === "Sin tope" ? 0.4 : 1,
                  }}
                  {...register("capQuantity", {
                    // QA-MTZ-002 #11: react-hook-form también pasa acá el
                    // `null` del valor inicial; `Number(null)` es 0 y
                    // disparaba la validación de un tope que no existe.
                    setValueAs: (value: unknown) =>
                      value === "" || value === null || value === undefined
                        ? null
                        : Number(value),
                  })}
                />
              </Field>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 16px",
            }}
          >
            <Field
              label="Vigente desde"
              error={
                serverError?.field === "validFrom" ||
                serverError?.field === "validity"
                  ? serverError.message
                  : errors.validFrom?.message
              }
            >
              <input
                type="date"
                style={inputStyle}
                {...register("validFrom")}
              />
            </Field>
            <Field
              label="Vigente hasta"
              hint="Vacío = indefinida."
              error={
                serverError?.field === "validity"
                  ? serverError.message
                  : undefined
              }
            >
              <input
                type="date"
                style={inputStyle}
                {...register("validUntil")}
              />
            </Field>
          </div>

          {scope === "member" ? (
            <div style={{ marginBottom: 16 }}>
              <span
                style={{
                  font: `600 12px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                Tipo de asignación
              </span>
              <div
                style={{ display: "flex", gap: 10, marginTop: 8 }}
                role="group"
                aria-label="Tipo de asignación"
              >
                {(["automatic", "manual"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={
                      "chip" + (watch("assignment") === value ? " on" : "")
                    }
                    style={{ padding: "10px 18px", fontSize: 13 }}
                    onClick={() =>
                      setValue("assignment", value, { shouldDirty: true })
                    }
                  >
                    {value === "automatic" ? "Automática" : "Manual"}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </fieldset>

        {/* F1 (retry-category review_finding, 2026-09-07): el toggle
            Estado queda FUERA del fieldset de arriba a propósito. El
            servidor exige `canInactivate` como permiso distinto de
            `canEdit` para el DELETE (api/point-rules.routes.ts:64-72), así
            que el botón "Inactivo" no puede depender de `canWrite`
            (=canCreate/canEdit): un fieldset disabled deshabilita a todos
            sus descendientes sin excepción, así que ese botón no puede
            vivir adentro si tiene que seguir habilitado para un perfil con
            canInactivate y sin canEdit. El fieldset no tiene borde ni
            padding propio (border:none, margin:0, padding:0), así que
            partirlo en dos no mueve ningún píxel: el patrón ya lo siguen
            members/users/catalogs/profiles con <Can action="canInactivate">
            envolviendo el control específico, independiente del resto del
            formulario. */}
        <div style={{ marginBottom: 16 }}>
          <span
            style={{
              font: `600 12px ${tokens.font.ui}`,
              color: tokens.text.secondary,
            }}
          >
            Estado
          </span>
          <div
            style={{ display: "flex", gap: 10, marginTop: 8 }}
            role="group"
            aria-label="Estado"
          >
            <button
              type="button"
              disabled={!canWrite}
              className={"chip" + (active ? " on" : "")}
              style={{ padding: "10px 18px", fontSize: 13 }}
              onClick={() => setValue("active", true, { shouldDirty: true })}
            >
              Activo
            </button>
            {isCreate ? (
              <button
                type="button"
                disabled={!canWrite}
                className={"chip" + (!active ? " on" : "")}
                style={{ padding: "10px 18px", fontSize: 13 }}
                onClick={() => setValue("active", false, { shouldDirty: true })}
              >
                Inactivo
              </button>
            ) : (
              <Can module="points_engine" action="canInactivate">
                <button
                  type="button"
                  className={"chip" + (!active ? " on" : "")}
                  style={{ padding: "10px 18px", fontSize: 13 }}
                  onClick={() => {
                    // A5 (human-resolution-spec-212.json): pasar de Activa a
                    // Inactiva en una regla EXISTENTE dispara la
                    // confirmación de impacto; nunca se guarda el toggle
                    // como un cambio de formulario más.
                    if (active) {
                      setPendingInactivate(true);
                      return;
                    }
                    setValue("active", false, { shouldDirty: true });
                  }}
                >
                  Inactivo
                </button>
              </Can>
            )}
          </div>
        </div>

        <fieldset
          disabled={!canWrite}
          style={{ border: "none", margin: 0, padding: 0 }}
        >
          {scope === "member" ? (
            <InfoNotice style={{ marginTop: 6 }}>
              {MEMBER_FUTURE_CHANGES_HELP}
            </InfoNotice>
          ) : (
            <InfoNotice style={{ marginTop: 6 }}>
              {REFERRAL_ANEXO_014_HELP}
            </InfoNotice>
          )}
        </fieldset>
      </EditorModal>
      <ImpactConfirmDialog
        open={pendingInactivate}
        eyebrow="Motor de Puntos"
        title="Confirmar inactivación de la regla"
        affectedCount={1}
        affectedLabel={CONFIRM_INACTIVATE_MESSAGE}
        onCancel={() => setPendingInactivate(false)}
        onConfirm={confirmInactivate}
        confirmLabel="Sí, inactivar"
        busy={mutations.inactivate.isPending}
      />
    </>
  );
}
